import type { ClientVATDeclarationStatusType } from '../../../../../../../generated/enums.ts';
import { byName, onDate, vatReturnPeriodLabel } from '../../VatReturn/utils/index.ts';
import type { ClientVatDeclarationStatus } from '../../models.ts';

export const VAT_DECLARATION_STATUS_LABELS: Record<ClientVATDeclarationStatusType, string> = {
    not_started: 'Niet gestart',
    open: 'Open',
    awaiting_approval: 'Wacht op goedkeuring',
    approved: 'Goedgekeurd',
    rejected: 'Afgewezen',
};

export const VAT_DECLARATION_LOAD_FAILED = 'Btw-status kon niet worden geladen';

const SORT_RANK: Record<ClientVATDeclarationStatusType, number> = {
    rejected: 0,
    open: 1,
    not_started: 2,
    awaiting_approval: 3,
    approved: 4,
};

export function vatDeclarationSortRank(declaration: ClientVatDeclarationStatus): number {
    return SORT_RANK[declaration.status];
}

export function notStartedWithoutDeclaration(businessId: string): ClientVatDeclarationStatus {
    return {
        business_id: businessId,
        status: 'not_started',
        snapshot_id: null,
        period_start: null,
        period_end: null,
        approval_requested_at: null,
        approval_answered_at: null,
        approval_answered_by_name: null,
        approval_rejection_reason: null,
        approval_withdrawn_at: null,
        approval_withdrawn_by_name: null,
    };
}

function openTooltip(declaration: ClientVatDeclarationStatus, period: string, clientName: string): string {
    if (declaration.approval_withdrawn_at) {
        return `${period}: verzoek ingetrokken${byName(declaration.approval_withdrawn_by_name)}${onDate(declaration.approval_withdrawn_at)}. Nog niet opnieuw verstuurd.`;
    }
    return `${period} is opgeslagen, maar nog niet ter goedkeuring verstuurd naar ${clientName}.`;
}

export function vatDeclarationLink(clientId: string, declaration: ClientVatDeclarationStatus): string {
    return declaration.snapshot_id
        ? `/business/${clientId}?tab=vat&snapshot=${declaration.snapshot_id}`
        : `/business/${clientId}?tab=vat`;
}

export function vatDeclarationStatusTooltip(declaration: ClientVatDeclarationStatus, clientName: string): string {
    if (declaration.period_start === null || declaration.period_end === null) {
        return 'Nog geen btw-aangifte opgeslagen.';
    }
    const period = vatReturnPeriodLabel(declaration.period_start, declaration.period_end);
    const answered = `${byName(declaration.approval_answered_by_name)}${onDate(declaration.approval_answered_at)}`;
    switch (declaration.status) {
        case 'not_started':
            return 'Nog geen btw-aangifte opgeslagen.';
        case 'open':
            return openTooltip(declaration, period, clientName);
        case 'awaiting_approval':
            return `${period} verstuurd naar ${clientName}${onDate(declaration.approval_requested_at)}. Nog geen antwoord.`;
        case 'approved':
            return `${period} goedgekeurd${answered}.`;
        case 'rejected':
            return declaration.approval_rejection_reason
                ? `${period} afgewezen${answered}: “${declaration.approval_rejection_reason}”`
                : `${period} afgewezen${answered}.`;
        default: {
            const unknown: never = declaration.status;
            throw new Error(`Unknown VAT declaration status: ${unknown}`);
        }
    }
}
